import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from '@safetyhub/ui';
import { adminApi } from '../../lib/api';
import { useToastStore } from '../../stores/toast';
import { ModalCancelButton } from '../ModalCancelButton';

interface FlagCreateModalProps {
  onClose: () => void;
}

export default function FlagCreateModal({ onClose }: FlagCreateModalProps) {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'BOOLEAN' | 'MULTIVARIATE'>('BOOLEAN');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [error, setError] = useState('');
  // 제출 버튼이 base footer(폼 밖)에 있어 `form` 속성으로 폼에 묶는다 — Enter 제출이 그대로 산다.
  const formId = useId();

  const mutation = useMutation({
    mutationFn: () =>
      adminApi.createFeatureFlag({
        key,
        name,
        description: description || undefined,
        type,
        tags: tags.length > 0 ? tags : undefined,
      }),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
        showToast('피처 플래그가 생성되었습니다.', 'success');
        onClose();
      } else {
        setError(result.error?.message || '생성에 실패했습니다.');
      }
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { error?: { message?: string } } } };
      setError(axiosErr?.response?.data?.error?.message || '생성 중 오류가 발생했습니다.');
    },
  });

  // Key 자동 변환: 소문자 + 하이픈
  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-');
    setKey(value);
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setTagInput('');
    }
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!key || !name) {
      setError('Key와 Name은 필수 항목입니다.');
      return;
    }

    mutation.mutate();
  };

  // 미저장 닫기 보호 — 생성 폼이라 기준선은 빈 폼이다(열자마자 dirty가 되지 않는다).
  // 생성 성공 시의 onClose는 base를 거치지 않으므로 확인 없이 닫힌다(의도).
  const isDirty =
    key.length > 0 ||
    name.length > 0 ||
    description.length > 0 ||
    type !== 'BOOLEAN' ||
    tagInput.trim().length > 0 ||
    tags.length > 0;

  // 부모가 열 때만 마운트하므로 open은 늘 true다. 닫는 길은 종전 그대로 열어 둔다(생성 중 잠금 없음).
  return (
    <Modal
      open
      onClose={onClose}
      title="새 피처 플래그"
      size="md"
      isDirty={isDirty}
      footer={
        <>
          <ModalCancelButton onCancel={onClose} />
          <Button type="submit" form={formId} variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? '생성 중...' : '생성'}
          </Button>
        </>
      }
    >
        <form id={formId} onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Key *
            </label>
            <input
              type="text"
              value={key}
              onChange={handleKeyChange}
              placeholder="feature-key-name"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              영문 소문자와 하이픈만 사용 가능
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="기능 이름"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="기능 설명 (선택)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'BOOLEAN' | 'MULTIVARIATE')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="BOOLEAN">Boolean</option>
              <option value="MULTIVARIATE">Multivariate</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="태그 입력 (Enter)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
              />
              <button
                type="button"
                onClick={handleAddTag}
                disabled={!tagInput.trim()}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 text-sm"
              >
                추가
              </button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </form>
    </Modal>
  );
}
